/* The part of dictation that decides what ends up in the box.

   Pure, so the browser quirks can be replayed in a test instead of on a
   client's phone. The browser SpeechRecognition API hands over a RESULTS LIST
   on every event — some entries final, the trailing ones still being revised —
   and the previous hook rebuilt the whole box from that list each time. That
   works on desktop Chrome and doubled phrases everywhere else:

   - iOS Safari ends recognition after every phrase. Restarting the same
     recognizer often re-emits the last phrase as a fresh final.
   - Android Chrome can fire a late onresult AFTER onend, carrying finals the
     hook had already moved into its committed text; and it can deliver a
     final that extends an earlier final ("we replaced the" → "we replaced the
     furnace") as a separate entry.

   So the box is now built from COMMITTED SEGMENTS, appended once each:
   - a result is consumed by index, so a re-delivered list adds nothing;
   - an event from a run that has ended is ignored (every run has a token);
   - a final that APPEARS ALREADY FINAL — no interim was ever shown at its
     index — and matches the last committed segment is dropped, and one that
     merely extends it replaces it.

   That last condition is what separates an echo from speech. A phrase the
   client really says twice is heard twice: each copy builds through interims
   at its own index before finalizing (a real capture: "meow", then " me" →
   " meow" → final). An echo is a replayed final with no interim behind it.

   De-duplication is by whole segment only. Word-level rules would eat
   legitimate repeats ("it was very, very tight"), and a dropped word is worse
   than a doubled one — the client notices one and not the other. */

export interface RecognitionResult { transcript: string; isFinal: boolean }

export interface DictationState {
  /** Text already in the box when the mic started; a trailing space is added when non-empty. */
  base: string;
  /** Final segments across every run of this dictation, in order, each committed once. */
  committed: string[];
  /** Token of the run whose events are accepted; anything else is a straggler. */
  run: number;
  /** Results of the current run with an index below this are already committed. */
  consumed: number;
  /** Highest index of the current run that has been seen as an interim — i.e. heard being spoken. */
  heard: number;
}

export function startDictation(current: string): DictationState {
  return { base: current ? `${current} ` : '', committed: [], run: 1, consumed: 0, heard: -1 };
}

const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim();

/* Add one final segment. `heard` means an interim was shown at this index
   first, so it is speech and is never de-duplicated. Whole-segment comparison
   only — see the header. The extension rule needs the earlier segment to be
   three words or more, because "No." followed by "No problem." is two
   statements, not one revised. */
function commit(committed: string[], seg: string, heard: boolean): string[] {
  const n = norm(seg);
  if (!n) return committed;
  const last = committed[committed.length - 1];
  if (last !== undefined && !heard) {
    const ln = norm(last);
    if (ln === n) return committed;
    if (ln.split(' ').length >= 3 && n.startsWith(`${ln} `)) return [...committed.slice(0, -1), seg.trim()];
  }
  return [...committed, seg.trim()];
}

/** One onresult event from run `run`. Returns the new state and what the box should show. */
export function applyResult(state: DictationState, run: number, results: RecognitionResult[]): { state: DictationState; text: string } {
  if (run !== state.run) return { state, text: render(state, '') };
  let committed = state.committed;
  let consumed = state.consumed;
  /* Finals sit at the front of the list and interims trail; consume finals in
     order from where the last event left off. A final behind a still-interim
     entry waits — endRun sweeps it up when the run closes. */
  while (consumed < results.length && results[consumed].isFinal) {
    committed = commit(committed, results[consumed].transcript, consumed <= state.heard);
    consumed++;
  }
  let heard = state.heard;
  for (let i = consumed; i < results.length; i++) if (!results[i].isFinal) heard = Math.max(heard, i);
  const interim = results.slice(consumed).filter((r) => !r.isFinal).map((r) => r.transcript.trim()).filter(Boolean).join(' ');
  const next = { ...state, committed, consumed, heard };
  return { state: next, text: render(next, interim) };
}

/** The run ended (the browser stopped, or we did). Commits any final the
    in-order walk had not reached, then opens the next run. */
export function endRun(state: DictationState, run: number, lastResults: RecognitionResult[]): DictationState {
  if (run !== state.run) return state;
  let committed = state.committed;
  for (let i = state.consumed; i < lastResults.length; i++) {
    if (lastResults[i].isFinal) committed = commit(committed, lastResults[i].transcript, i <= state.heard);
  }
  return { ...state, committed, run: state.run + 1, consumed: 0, heard: -1 };
}

export function render(state: DictationState, interim: string): string {
  const spoken = [...state.committed, interim.trim()].filter(Boolean).join(' ');
  return `${state.base}${spoken}`;
}
